// src/pages/usuarios/UsuarioDetallePage.tsx

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { userAdminApi } from "../../api/userAdminApi";
import { AdminUser, UserCoupon } from "../../types";
import { getErrorMessage } from "../../utils/errors";
import {
  formatDate,
  formatDateTime,
  formatPhone,
  fullName,
} from "../../utils/format";
import { Alert, useFlash } from "../../components/ui/Alert";
import { AccountBadge, PlanBadge } from "../../components/ui/Badges";
import UserAvatar from "../../components/ui/UserAvatar";
import {
  UnassignCouponModal,
  UserAction,
  UserActionModal,
} from "../../components/usuarios/UserActionModals";
import diamondImg from "../../assets/images/diamond.png";
import planFreeImg from "../../assets/images/planfree.png";
import cuponImg from "../../assets/images/cupon.png";

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="detail-item">
      <div className="label">{label}</div>
      <div className="value">{value || "—"}</div>
    </div>
  );
}

// Mismo ícono que usa la app en el perfil según el plan
function planImage(status: string | null) {
  if (status === "pagado") return diamondImg;
  if (status === "cupon") return cuponImg;
  return planFreeImg;
}

export default function UsuarioDetallePage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const flash = useFlash();

  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [action, setAction] = useState<UserAction | null>(null);
  const [coupons, setCoupons] = useState<UserCoupon[] | null>(null);
  const [couponsError, setCouponsError] = useState("");
  const [unassigning, setUnassigning] = useState<UserCoupon | null>(null);

  const loadCoupons = useCallback(async () => {
    setCouponsError("");
    try {
      const res = await userAdminApi.cupones(id);
      setCoupons(res.content);
    } catch (err) {
      setCouponsError(
        getErrorMessage(err, "No se pudieron cargar los cupones del usuario."),
      );
    }
  }, [id]);

  useEffect(() => {
    loadCoupons();
  }, [loadCoupons]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await userAdminApi.obtener(id);
      setUser(res.content);
    } catch (err) {
      setLoadError(getErrorMessage(err, "No se pudo cargar el usuario."));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const onActionDone = (updated: AdminUser, message: string) => {
    const couponsChanged = action === "coupon" || unassigning !== null;
    setAction(null);
    setUnassigning(null);
    setUser(updated);
    flash.show("success", message);
    if (couponsChanged) loadCoupons();
  };

  const goBack = () =>
    window.history.length > 1 ? navigate(-1) : navigate("/usuarios");

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner-border" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="tp-card">
        <div className="empty-state">
          <i className="bi bi-person-exclamation" />
          {loadError || "El usuario no existe."}
          <div className="mt-3">
            <button
              className="btn-tp-outline"
              onClick={() => navigate("/usuarios")}
            >
              <i className="bi bi-arrow-left" /> Volver a usuarios
            </button>
          </div>
        </div>
      </div>
    );
  }

  const name = fullName(user.firstName, user.lastName, user.username);

  return (
    <>
      <div className="page-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <button
            className="btn btn-link p-0 mb-1"
            style={{
              color: "var(--tp-muted)",
              fontSize: "0.85rem",
              textDecoration: "none",
            }}
            onClick={goBack}
          >
            <i className="bi bi-arrow-left me-1" /> Usuarios
          </button>
          <h1 className="page-title">{name}</h1>
          <p className="page-subtitle">@{user.username}</p>
        </div>
      </div>

      {flash.node}

      <div className="user-detail-grid">
        {/* ── Perfil ── */}
        <div className="tp-card">
          <div className="tp-card-body text-center">
            <div className="d-flex justify-content-center mb-3">
              <UserAvatar
                imagePath={user.imagePath}
                firstName={user.firstName}
                lastName={user.lastName}
                size="lg"
              />
            </div>
            <div
              style={{
                fontWeight: 700,
                color: "var(--tp-primary)",
                fontSize: "1.05rem",
              }}
            >
              {name}
            </div>
            <div className="text-muted-tp mb-2">{user.email}</div>
            <div className="d-flex justify-content-center gap-2 flex-wrap mb-3">
              <AccountBadge enabled={user.enabled} />
              {user.admin && (
                <span className="badge-estado badge-pendiente">
                  <i className="bi bi-shield-fill-check" /> Admin
                </span>
              )}
            </div>

            {!user.enabled && user.inactiveReason && (
              <div
                className="tp-alert error text-start"
                style={{ marginBottom: 16 }}
              >
                <i className="bi bi-info-circle-fill" />
                <span>
                  <strong>Motivo de desactivación:</strong>{" "}
                  {user.inactiveReason}
                </span>
              </div>
            )}

            <div className="d-grid gap-2">
              {user.enabled ? (
                <button
                  className="btn-tp-danger justify-content-center"
                  onClick={() => setAction("deactivate")}
                >
                  <i className="bi bi-slash-circle" /> Desactivar cuenta
                </button>
              ) : (
                <button
                  className="btn-tp-success justify-content-center"
                  onClick={() => setAction("activate")}
                >
                  <i className="bi bi-check-lg" /> Activar cuenta
                </button>
              )}
              <button
                className="btn-tp-morado justify-content-center"
                onClick={() => setAction("coupon")}
              >
                <i className="bi bi-ticket-perforated" /> Asignar cupón
              </button>
            </div>
          </div>
        </div>

        <div className="d-flex flex-column gap-3">
          {/* ── Suscripción ── */}
          <div className="tp-card">
            <div className="tp-card-header">
              <div className="tp-card-title">Suscripción</div>
              <PlanBadge status={user.paymentStatus} />
            </div>
            <div className="tp-card-body">
              {user.paymentStatus ? (
                <>
                  <div className="plan-highlight mb-3">
                    <img src={planImage(user.paymentStatus)} alt="" />
                    <div>
                      <div
                        style={{
                          fontWeight: 700,
                          color: "var(--tp-primary)",
                          fontSize: "1.1rem",
                        }}
                      >
                        {user.remainingDays && user.remainingDays > 0
                          ? `Le quedan ${user.remainingDays} días`
                          : "Sin días vigentes"}
                      </div>
                      <div className="text-muted-tp">
                        Vence el {formatDate(user.subscriptionExpirationDate)}
                      </div>
                    </div>
                  </div>
                  <div className="detail-list">
                    <Detail
                      label="Inicio"
                      value={formatDate(user.subscriptionStartDate)}
                    />
                    <Detail
                      label="Vencimiento"
                      value={formatDate(user.subscriptionExpirationDate)}
                    />
                    <Detail
                      label="Premium activo"
                      value={user.subscriptionActive ? "Sí" : "No"}
                    />
                    <Detail
                      label="Cupón aplicado"
                      value={
                        user.appliedCouponCode ? (
                          <span className="code-chip">
                            {user.appliedCouponCode}
                          </span>
                        ) : null
                      }
                    />
                  </div>
                </>
              ) : (
                <div className="empty-state py-3">
                  <i className="bi bi-credit-card-2-front" />
                  El usuario no tiene suscripción registrada
                </div>
              )}
            </div>
          </div>

          {/* ── Cupones del usuario ── */}
          <div className="tp-card">
            <div className="tp-card-header">
              <div className="tp-card-title">Cupones del usuario</div>
              <button
                className="btn-tp-ghost"
                style={{ color: "var(--tp-morado)" }}
                onClick={() => setAction("coupon")}
              >
                <i className="bi bi-plus-lg" /> Asignar
              </button>
            </div>
            {couponsError ? (
              <div className="tp-card-body">
                <Alert type="error" message={couponsError} />
              </div>
            ) : !coupons ? (
              <div className="text-center py-4">
                <span
                  className="spinner-border spinner-border-sm"
                  style={{ color: "var(--tp-primary)" }}
                />
              </div>
            ) : coupons.length === 0 ? (
              <div className="empty-state py-4">
                <i className="bi bi-ticket-perforated" />
                El usuario no tiene cupones
              </div>
            ) : (
              <div className="table-responsive">
                <table className="tp-table">
                  <thead>
                    <tr>
                      <th>Cupón</th>
                      <th>Descuento</th>
                      <th>Fecha</th>
                      <th>Origen</th>
                      <th style={{ textAlign: "right" }}>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coupons.map((c) => (
                      <tr key={c.userCouponId}>
                        <td>
                          <span className="code-chip">{c.code}</span>
                          {c.inEffect && (
                            <div className="mt-1">
                              <span className="badge-estado badge-cupon">
                                Vigente
                              </span>
                            </div>
                          )}
                        </td>
                        <td>
                          <strong>{c.discountPercent}%</strong>
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {formatDate(c.usedAt)}
                        </td>
                        <td className="text-muted-tp">
                          {c.assignedByAdmin
                            ? "Asignado en el panel"
                            : "Aplicado en la app"}
                        </td>
                        <td>
                          <div className="table-action-group">
                            {c.removable ? (
                              <button
                                className="btn-tp-ghost"
                                style={{ color: "var(--tp-red)" }}
                                onClick={() => setUnassigning(c)}
                                title="Desasignar cupón"
                              >
                                <i className="bi bi-x-lg" /> Desasignar
                              </button>
                            ) : (
                              <span
                                className="text-muted-tp"
                                title="Los descuentos parciales ya se cobraron en Mercado Pago"
                              >
                                No aplica
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Datos personales ── */}
          <div className="tp-card">
            <div className="tp-card-header">
              <div className="tp-card-title">Datos del usuario</div>
            </div>
            <div className="tp-card-body">
              <div className="detail-list">
                <Detail label="Nombres" value={user.firstName} />
                <Detail label="Apellidos" value={user.lastName} />
                <Detail label="Usuario" value={user.username} />
                <Detail label="Email" value={user.email} />
                <Detail
                  label="Celular"
                  value={formatPhone(user.countryCode, user.phoneNumber)}
                />
                <Detail label="Identificación" value={user.identification} />
                <Detail
                  label="Fecha de registro"
                  value={formatDateTime(user.dateRegister)}
                />
                <Detail
                  label="Estado de verificación"
                  value={user.verificationStatus}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {action && (
        <UserActionModal
          action={action}
          user={user}
          onClose={() => setAction(null)}
          onDone={onActionDone}
        />
      )}
      {unassigning && (
        <UnassignCouponModal
          item={unassigning}
          onClose={() => setUnassigning(null)}
          onDone={onActionDone}
        />
      )}
    </>
  );
}
